/**
 * Wings -- the Expert tier's entry point.
 *
 * Both are short forcing arguments rather than pattern-counting: a pivot cell
 * with two (XY) or three (XYZ) candidates is linked to two "pincer" cells, and
 * whichever value the pivot takes, one pincer is forced to Z. So any cell that
 * can see every cell able to hold that Z loses it.
 */

import { CELLS, EMPTY, cellName, type Digit } from "../grid";
import { digitsOf, maskOf, popcount, soleDigit } from "../candidates";
import { peerMatrix } from "../constraints";
import type { Context, Elimination, Step, Technique } from "./types";

/** Cells that see every one of `viewers` and are none of them. */
function commonNeighbours(
  matrix: Uint8Array,
  viewers: readonly number[],
  candidates: Int16Array,
  board: Uint8Array,
  bit: number,
): number[] {
  const out: number[] = [];
  for (let cell = 0; cell < CELLS; cell++) {
    if (board[cell] !== EMPTY) continue;
    if ((candidates[cell]! & bit) === 0) continue;
    if (viewers.includes(cell)) continue;
    if (viewers.every((viewer) => matrix[viewer * CELLS + cell] === 1)) out.push(cell);
  }
  return out;
}

export const xyWing: Technique = {
  id: "xy-wing",
  name: "XY-Wing",
  tier: "expert",
  ser: 4.2,
  find({ board, candidates, cs }: Context): Step | null {
    const matrix = peerMatrix(cs);

    for (let pivot = 0; pivot < CELLS; pivot++) {
      if (board[pivot] !== EMPTY || popcount(candidates[pivot]!) !== 2) continue;
      const [x, y] = digitsOf(candidates[pivot]!) as [Digit, Digit];

      for (const first of cs.peers[pivot]!) {
        if (board[first] !== EMPTY || popcount(candidates[first]!) !== 2) continue;
        const shared = candidates[first]! & candidates[pivot]!;
        if (popcount(shared) !== 1) continue;

        const linkA = soleDigit(shared); // the pivot digit this pincer covers
        const z = soleDigit(candidates[first]! & ~shared);
        if (z === x || z === y) continue;
        const linkB = linkA === x ? y : x;
        const wanted = maskOf(linkB) | maskOf(z);

        for (const second of cs.peers[pivot]!) {
          if (second === first || board[second] !== EMPTY) continue;
          if (candidates[second] !== wanted) continue;

          const bit = maskOf(z);
          const targets = commonNeighbours(matrix, [first, second], candidates, board, bit);
          if (targets.length === 0) continue;

          const eliminations: Elimination[] = targets.map((cell) => ({ cell, digit: z }));
          return {
            technique: "xy-wing",
            pattern: [pivot, first, second],
            digits: [z],
            placements: [],
            eliminations,
            explanation:
              `XY-Wing: ${cellName(pivot)} is {${x},${y}}, with ${cellName(first)} = ` +
              `{${linkA},${z}} and ${cellName(second)} = {${linkB},${z}}. Whichever of ` +
              `${x}/${y} the pivot takes, one of the two pincers is forced to ${z}, so ${z} ` +
              `can be removed from any cell seeing both: ` +
              `${targets.map(cellName).join(", ")}.`,
          };
        }
      }
    }
    return null;
  },
};

export const xyzWing: Technique = {
  id: "xyz-wing",
  name: "XYZ-Wing",
  tier: "expert",
  ser: 4.4,
  find({ board, candidates, cs }: Context): Step | null {
    const matrix = peerMatrix(cs);

    for (let pivot = 0; pivot < CELLS; pivot++) {
      if (board[pivot] !== EMPTY || popcount(candidates[pivot]!) !== 3) continue;
      const pivotMask = candidates[pivot]!;
      const peers = cs.peers[pivot]!;

      for (let i = 0; i < peers.length; i++) {
        const first = peers[i]!;
        if (board[first] !== EMPTY || popcount(candidates[first]!) !== 2) continue;
        if ((candidates[first]! & pivotMask) !== candidates[first]!) continue;

        for (let j = i + 1; j < peers.length; j++) {
          const second = peers[j]!;
          if (board[second] !== EMPTY || popcount(candidates[second]!) !== 2) continue;
          if ((candidates[second]! & pivotMask) !== candidates[second]!) continue;
          // The two pincers must together span the pivot and overlap in one digit.
          if ((candidates[first]! | candidates[second]!) !== pivotMask) continue;
          const shared = candidates[first]! & candidates[second]!;
          if (popcount(shared) !== 1) continue;

          const z = soleDigit(shared);
          const bit = maskOf(z);
          // Unlike XY-Wing the pivot itself can hold Z, so targets must see it too.
          const targets = commonNeighbours(
            matrix,
            [pivot, first, second],
            candidates,
            board,
            bit,
          );
          if (targets.length === 0) continue;

          return {
            technique: "xyz-wing",
            pattern: [pivot, first, second],
            digits: [z],
            placements: [],
            eliminations: targets.map((cell) => ({ cell, digit: z })),
            explanation:
              `XYZ-Wing: ${cellName(pivot)} is {${digitsOf(pivotMask).join(",")}}, with ` +
              `pincers ${cellName(first)} = {${digitsOf(candidates[first]!).join(",")}} and ` +
              `${cellName(second)} = {${digitsOf(candidates[second]!).join(",")}}. All three ` +
              `can hold ${z}, and one of them must, so ${z} can be removed from any cell ` +
              `seeing all three: ${targets.map(cellName).join(", ")}.`,
          };
        }
      }
    }
    return null;
  },
};
